import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getChatDocumentById,
  getChatsByIds,
  getChatsByOrderId,
  getChatsBySearchQuery,
  getChatsForOrderDetails,
  getChatsQuery,
  MainFilters,
  messagesRef,
} from '@util/firestore/messages';
import { getOffersByStatePaginated } from '@util/firestore/offers';
import { useAuth } from 'context/AuthContext';
import { useConversations } from 'context/ConversationsContext';
import {
  getDocs,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { ChatDocument } from 'models/chat';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { getOrderById } from '@util/firestore/orders';
import { OrderDocument } from 'models/order';
import { limit as limitFn } from 'firebase/firestore';
import {
  getUserByUsername,
  getUserByUsernameSlug,
} from '@util/firestore/users';
export interface UseTabChatsReturn {
  chats: ChatDocument[];
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
}

export const useTabChats = (tab: MainFilters): UseTabChatsReturn => {
  const { userDoc } = useAuth();
  const pathname = usePathname();
  const { debouncedSearchQuery } = useConversations();
  const [chats, setChats] = useState<ChatDocument[]>([]);
  const [limit, setLimit] = useState(20);
  const hasMore = chats.length === limit;
  const isFilteredOffers = [
    'offers_pending',
    'offers_accepted',
    'offers_declined',
    'offers_expired',
  ].includes(tab);
  const isOrderDetail =
    pathname?.includes('purchases') || pathname?.includes('orders');
  const orderId = pathname?.split('/').pop();
  const { data: order } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrderById(orderId as string),
    enabled: !!orderId && isOrderDetail,
  });
  const { data: chatsForOrderDetail = [] } = useQuery({
    queryKey: ['chatsForOrderDetail', orderId],
    queryFn: () => getChatsForOrderDetails(order as OrderDocument),
    enabled: !!order && isOrderDetail,
  });

  const isProfile = pathname?.includes('profile');
  const username_slug = pathname?.split('/').pop();
  const {
    data: chatsForProfile,
    fetchNextPage: fetchNextPageForProfile,
    hasNextPage: hasNextPageForProfile,
    isFetchingNextPage: isFetchingNextPageForProfile,
  } = useInfiniteQuery({
    queryKey: ['chatsForProfile', username_slug],
    queryFn: async ({ pageParam }) => {
      const user = await getUserByUsernameSlug(username_slug as string);
      if (!user) return [];
      const q = query(
        messagesRef,
        where('uids', 'array-contains', user.uid),
        orderBy('last_time', 'desc'),
        ...(pageParam ? [startAfter(pageParam)] : []),
        limitFn(20)
      );
      const snap = await getDocs(q);
      return snap.docs.map((doc) => doc.data() as ChatDocument);
    },
    enabled: !!username_slug && isProfile,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined;
      return lastPage[lastPage.length - 1].last_time;
    },
    initialPageParam: 0,
  });

  const isSharedChat =
    pathname?.includes('messages') && pathname.split('/').pop() !== 'messages';
  const sharedChatId = pathname?.split('/').pop();
  const { data: sharedChat } = useQuery({
    queryKey: ['sharedChat', sharedChatId],
    queryFn: () => getChatDocumentById(sharedChatId as string),
    enabled: !!sharedChatId && isSharedChat,
  });
  const loadMore = () => {
    if (isOrderDetail && order) return; /// all chats loaded
    if (isSharedChat && sharedChat) return; // only one chat here and its loaded
    if (isProfile && hasNextPageForProfile && !isFetchingNextPageForProfile) {
      fetchNextPageForProfile();
    } else if (isFilteredOffers && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    } else if (hasMore && !isLoading) {
      setLimit((prev) => prev + 20);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const stateNum = {
    offers_pending: 0,
    offers_accepted: 1,
    offers_declined: 2,
    offers_expired: 3,
  } as const;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['offers', userDoc?.uid, tab],
      queryFn: ({ pageParam }) =>
        getOffersByStatePaginated({
          userDoc,
          state: stateNum[tab as keyof typeof stateNum],
          last: pageParam as number,
          l: limit,
        }),
      getNextPageParam: (lastPage) => {
        if (lastPage.length < limit) return undefined;
        return lastPage[lastPage.length - 1].created;
      },
      initialPageParam: 0,
      enabled: isFilteredOffers,
    });

  const { data: chatBySearchQuery = [] } = useQuery({
    queryKey: [
      'chatByOrderNum',
      userDoc?.uid,
      debouncedSearchQuery,
      chats.map((chat) => chat.id),
    ],
    queryFn: async () => {
      const snap = await getDocs(
        getChatsBySearchQuery(debouncedSearchQuery, userDoc)
      );
      const chatsByCaseNumOrOrderNum = snap.docs.map(
        (doc) => doc.data() as ChatDocument
      );
      const filteredChats = chats.filter(
        (chat) =>
          chat.last_msg
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          chat.order_num
            ?.toString()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          chat.case_num
            ?.toString()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          chat.messages.some(
            (message) =>
              (!message.is_auto || message.auto_recipient === userDoc?.uid) &&
              message.content
                ?.toLowerCase()
                .includes(debouncedSearchQuery.toLowerCase())
          )
      );
      // remove duplicates
      const unqiueChats = [
        ...filteredChats,
        ...chatsByCaseNumOrOrderNum,
      ].filter(
        (chat, index, self) => index === self.findIndex((t) => t.id === chat.id)
      );
      return unqiueChats;
    },
    enabled: !!debouncedSearchQuery,
  });
  useEffect(() => {
    if (!userDoc) return;
    if (isOrderDetail || isSharedChat || isProfile) return; // these chats are already loaded
    if (isFilteredOffers) {
      const ids = data?.pages
        .flatMap((page) => page.map((offer) => offer.chat_id ?? ''))
        .filter((id): id is string => id !== undefined);
      if (!ids?.length) return;
      setIsLoading(true);

      // first 30 are realtime, rest are just fetched
      const realtimeIds = ids?.slice(0, 30);
      const q = query(
        messagesRef,
        where('id', 'in', realtimeIds),
        ...(userDoc.roles?.support
          ? []
          : [where('uids', 'array-contains', userDoc.uid)])
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const chats = snapshot.docs.map((doc) => doc.data() as ChatDocument);
        const remainingChats = ids?.slice(30);
        if (remainingChats?.length) {
          getChatsByIds(remainingChats, userDoc).then((remainingChats) => {
            setChats([...chats, ...remainingChats]);
            setIsLoading(false);
          });
        } else {
          setChats(chats);
          setIsLoading(false);
        }
      });
      return () => {
        unsubscribe();
      };
    } else {
      setIsLoading(true);

      const chatsQuery = getChatsQuery({
        userDoc,
        mainFilter: tab,
        numOfChats: limit,
      });
      const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
        const chats = snapshot.docs.map((doc) => doc.data() as ChatDocument);
        setChats(chats);
        setIsLoading(false);
      });
      return () => {
        unsubscribe();
      };
    }
  }, [tab, userDoc, limit, isFilteredOffers, data?.pages, isOrderDetail]);
  return {
    chats: (isSharedChat && sharedChat
      ? [sharedChat]
      : debouncedSearchQuery
      ? chatBySearchQuery
      : isOrderDetail
      ? chatsForOrderDetail
      : isProfile
      ? chatsForProfile?.pages.flat() ?? []
      : chats
    ).sort((a, b) => b.last_time - a.last_time), // FE filter since unread is not sorted in query
    loadMore,
    hasMore,
    isLoading,
  };
};
